"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import Button, { ButtonVariant } from "@/app/(components)/generics/Button/Button";
import { Input, TextInputType } from "@/app/(components)/generics/inputs/default/Input/Input";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { modelPreviewUrl } from "@/app/(components)/specifics/library/Library/Library.logic";
import { getInsoleModels } from "@/app/actions/insole-model/action";
import { createInsolePair } from "@/app/actions/insole-pair/action";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { T, TErrors } from "@/app/translations/Translations";
import { PublicInsoleModel } from "@/app/types/PublicInsoleModel";
import { PublicPatient } from "@/app/types/PublicPatient";
import { Routes } from "@/app/vars/Routes";
import styles from "./AddInsolePair.module.css";

type Props = { lang: string; patient: PublicPatient; onSuccess?: () => void };

export default function AddInsolePair(props: Props) {
  const { lang, patient, onSuccess } = props;

  const { closeModal } = ModalStore();

  const router = useRouter();

  const [models, setModels] = useState<PublicInsoleModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState("");
  const [data, setData] = useState({ name: "", notes: "" });
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});

  const fetchModels = useCallback(async () => {
    setLoading(true);
    const result = await getInsoleModels();
    setModels(result);
    setSelectedUuid(result[0]?.uuid ?? "");
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const handleAdd = async () => {
    setFieldErrors({});

    if (!data.name.trim()) {
      setFieldErrors(TErrors({ name: TKey.ERROR_REQUIRED }, lang));
      return;
    }
    if (!selectedUuid) {
      return;
    }

    setCreating(true);
    const result = await createInsolePair(patient.uuid, {
      name: data.name,
      notes: data.notes,
      modelUuid: selectedUuid,
    });
    setCreating(false);

    if (!result.success) {
      return;
    }

    // Go straight to the configurator for the new pair. The modal closes itself
    // on the pathname change (Modal listens to it).
    if (result.uuid) {
      router.push(Routes.PROTECTED.CONFIGURATOR(lang, { pair: result.uuid }));
      return;
    }

    onSuccess?.();
    closeModal();
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <PointsLoader dark delay />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Input
        data={data}
        dataKey="name"
        label={T(TKey.INSOLE_PAIR_NAME, lang) as string}
        lang={lang}
        required
        showRequiredLabel
        placeholder={T(TKey.INSOLE_PAIR_NAME, lang) as string}
        onChange={(d) => setData({ ...d })}
        errorMessage={fieldErrors.name}
      />

      <Input
        data={data}
        dataKey="notes"
        type={TextInputType.TEXTAREA}
        label={T(TKey.INSOLE_PAIR_NOTES, lang) as string}
        lang={lang}
        showRequiredLabel
        placeholder=""
        onChange={(d) => setData({ ...d })}
      />

      <div className={styles.field}>
        <span className={styles.label}>{T(TKey.CHOOSE_INSOLE_MODEL, lang)}</span>
        {models.length === 0 ? (
          <p className={styles.empty}>{T(TKey.NO_INSOLE_MODEL_AVAILABLE, lang)}</p>
        ) : (
          <div className={styles.models}>
            {models.map((model) => (
              <div
                key={model.uuid}
                className={`${styles.modelTile} ${selectedUuid === model.uuid ? styles.modelTileSelected : ""}`}
                onClick={() => setSelectedUuid(model.uuid)}
              >
                <div className={styles.modelPreview}>
                  <Image
                    src={modelPreviewUrl(model)}
                    alt={model.name}
                    fill
                    sizes="140px"
                    className={styles.modelImage}
                  />
                </div>
                <span className={styles.modelName}>{model.name}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.actions}>
        <Button variant={ButtonVariant.SECONDARY} onClick={closeModal}>
          {T(TKey.CANCEL, lang)}
        </Button>
        <Button variant={ButtonVariant.PRIMARY} loading={creating} onClick={handleAdd}>
          {T(TKey.ADD_INSOLE_PAIR, lang)}
        </Button>
      </div>
    </div>
  );
}
